/**
 * Single source of truth for the 4 Agency Blog categories on the
 * frontend. Mirrors ``BLOG_CATEGORIES`` in ``/app/backend/blog.py``.
 *
 * The taxonomy is small and fixed (4 verticals), so keeping the labels
 * in code — not in Mongo — means they redeploy atomically with the rest
 * of the UI. To add a new category: edit BOTH the backend list AND this
 * file.
 *
 * ``intro`` is a 2-paragraph human-readable summary surfaced on the
 * /blog/category/<slug> page. It lifts the page's text-to-HTML ratio
 * (an SEO factor flagged by Semrush) and gives the category a real
 * keyword target instead of a 12-word tagline.
 */

export const CATEGORY_LIST = [
    {
        slug: "health",
        label: "Medicare, Managed Care, Medigap & Other Programs",
        shortLabel: "Medicare & Health",
        tagline:
            "Medicare, Medigap, Medicaid, and individual & family plans.",
        seoTitle:
            "Medicare & Managed Care Insurance Insights — Medigap, Medicaid, ACA Marketplace",
        seoDescription:
            "Medicare, Medigap, Medicaid, and ACA Marketplace updates from a licensed multi-state insurance agency.",
        // SEO-rich H1 used only on the category landing page. The shorter
        // `shortLabel` is what users see on tiles / breadcrumbs.
        seoH1:
            "Medicare & Managed Care Insurance Insights — Medigap, Medicaid, ACA Marketplace",
        intro: [
            "Health insurance changes every plan year, and every state writes its own rules on top of the federal baseline. Our Health category tracks the moving pieces that actually affect what you pay and what gets paid: Medicare Advantage star-rating changes, Part D drug-tier overhauls, Medigap underwriting windows, Medicaid work requirements, and the ACA Marketplace's quietly-shifting subsidy cliffs.",
            "Every article is written or reviewed by a licensed producer placing real clients in NY, NJ, PA, CT, NC, FL, and TX — not by anonymous content marketers. Use these notes to figure out what to ask, then bring the question to a real conversation with a licensed agent.",
        ],
        // Category-level FAQ accordion rendered at the bottom of the
        // page. Surfaced to Google as FAQPage JSON-LD for rich-result
        // eligibility on long-tail Medicare/Medigap/Medicaid queries.
        faqHeading: "Common Medicare, Medigap & ACA questions.",
        faqs: [
            {
                question:
                    "What's the difference between Medicare Advantage (Part C) and Original Medicare with Medigap?",
                answer:
                    "Original Medicare (Parts A and B) is the federal program; you can add a Medigap (Medicare Supplement) policy and a stand-alone Part D drug plan to fill the cost-sharing gaps and cap your out-of-pocket exposure. Medicare Advantage (Part C) bundles A, B, and usually D into a single private plan with a built-in provider network and an annual out-of-pocket maximum. Advantage plans are often $0 premium but route care through HMO/PPO networks; Original Medicare + Medigap costs more monthly but lets you see any provider that accepts Medicare nationwide.",
            },
            {
                question:
                    "When can I switch Medicare plans without medical underwriting?",
                answer:
                    "Every fall during the Annual Election Period (October 15 – December 7) you can switch between Medicare Advantage and Original Medicare, change Advantage plans, or join/change a Part D drug plan with no health questions asked. Medicare Advantage members get a second window — the Medicare Advantage Open Enrollment Period (January 1 – March 31) — to switch Advantage plans or drop Advantage and return to Original Medicare. Medigap is the exception: outside your six-month Medigap Open Enrollment Period (the six months that start the month you turn 65 and are enrolled in Part B) most states allow carriers to medically underwrite, which can raise the premium or deny coverage.",
            },
            {
                question:
                    "Do I still need Medigap if I already have employer or retiree health insurance?",
                answer:
                    "Usually only if you're keeping that coverage as primary while delaying Medicare Part B. Once you enroll in Part B and Medicare becomes primary, employer plans often shrink to a secondary or coordination-of-benefits role — and the deductibles, coinsurance, and lifetime caps Medicare leaves behind can be substantial. A licensed agent should review your existing summary of benefits before you decide, because dropping a generous retiree plan to add Medigap (or vice versa) is almost always irreversible.",
            },
            {
                question:
                    "Does Medicaid cover what Medicare doesn't, and can I have both?",
                answer:
                    "Yes — beneficiaries who qualify for both programs are called dual-eligibles. Medicare pays first for hospital, physician, and drug care; Medicaid then covers Medicare's deductibles, coinsurance, Part B premium (through Medicare Savings Programs), and benefits Medicare excludes entirely such as long-term nursing-home care, in-home personal care, and many vision/dental services. Eligibility, income limits, and benefits vary state by state, and 2026 brought new federal work-requirement rules in several states.",
            },
            {
                question:
                    "What changed for ACA Marketplace subsidies in 2026?",
                answer:
                    "The enhanced premium tax credits that lifted the income ceiling above 400% of the federal poverty level — first enacted in 2021 and extended through 2025 — were scheduled to sunset at the end of 2025. Going into Plan Year 2026, eligibility partially reverts to the pre-2021 cliff in several states unless Congress acts. We track the actual subsidy schedule per state on our Medicaid/ACA posts and update them when CMS publishes new guidance; the practical impact is that households around 400-500% FPL should re-shop coverage this fall rather than auto-renew.",
            },
            {
                question:
                    "I'm new to Medicare. Where should I actually start?",
                answer:
                    "Three steps: (1) confirm your Initial Enrollment Period window — it runs three months before through three months after your 65th-birthday month — and enroll in Part B unless you have creditable employer coverage; (2) decide between Original Medicare + Medigap + Part D versus Medicare Advantage by listing your current doctors and prescriptions and checking which path covers both; (3) talk to a licensed agent before you sign anything. We don't charge for the conversation, and the wrong plan choice in the first six months can cost thousands later when underwriting kicks in.",
            },
        ],
    },
    {
        slug: "personal",
        label: "Personal",
        tagline:
            "Home, auto, flood, and other personal-lines coverage.",
        seoTitle:
            "Personal Insurance Insights — Home, Auto, Flood & Umbrella",
        seoDescription:
            "Home, auto, flood, and other personal-lines coverage updates for multi-state homeowners and drivers.",
        intro: [
            "Personal-lines insurance — homeowners, condo, renters, auto, flood, and umbrella — is where most households interact with the insurance market the most. It's also where the cheapest quote is rarely the right answer. Our Personal category covers wind/flood/storm coverage on the coast, hail and tornado exposure in the interior, agreed-value vs. actual-cash-value on collector cars, and the umbrella stack that should sit on top of all of it.",
            "We write for actual homeowners and drivers, not for SEO bots. If an article surfaces a question that fits your situation, use the subscribe block or contact form to talk it through with a licensed agent who can quote it across multiple carriers.",
        ],
    },
    {
        slug: "business",
        label: "Business",
        tagline:
            "Workers' comp, general liability, BOP, and commercial auto.",
        seoTitle:
            "Business Insurance Insights — Workers' Comp, GL, BOP & Commercial Auto",
        seoDescription:
            "Workers' comp, general liability, BOP, and commercial auto guidance for small and mid-sized businesses.",
        intro: [
            "Small-business insurance is dense — workers' compensation rules vary by state and headcount, general liability and BOP get conflated, commercial auto has its own underwriting universe, and most landlords now require certificates with very specific endorsements. Our Business category breaks the questions apart so an owner-operator can walk into a renewal conversation knowing what to ask.",
            "Every post is grounded in real placements across NY, NJ, PA, CT, NC, FL, and TX and reviewed by a licensed producer. When an article hits close to your situation, the fastest next step is to send a one-line message to a real agent — we typically respond within one business day.",
        ],
    },
    {
        slug: "special-risk",
        label: "Special Risk",
        tagline:
            "Cyber, specialty, and other complex risk placements.",
        seoTitle:
            "Special Risk Insurance Insights — Cyber, E&S & Specialty Placements",
        seoDescription:
            "Cyber liability, specialty E&S placements, and complex risk insurance commentary from licensed agents.",
        intro: [
            "Special Risk is where the standard market says \"we don't write that\" and a real agent has to know which specialty carriers do. Our Special Risk category covers cyber liability and ransomware coverage, professional liability for non-traditional services, vacant-property and high-value coastal placements, event and short-term policies, and the surplus-lines paperwork that has to be right or the carrier won't pay.",
            "These are the placements where a 15-minute conversation with a licensed agent saves a 90-day claim fight later. Read the article, then book the call.",
        ],
    },
];

export const CATEGORY_META = Object.fromEntries(
    CATEGORY_LIST.map((c) => [c.slug, c])
);

export const DEFAULT_CATEGORY = "business";

export function getCategory(slug) {
    return CATEGORY_META[slug] || CATEGORY_META[DEFAULT_CATEGORY];
}
